import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logout } from '@/auth/logout';
import { Drawer } from '@/components/ui/drawer';
import { TourProvider } from '@/features/tour/TourProvider';
import { Banners } from './Banners';
import { CommandPalette } from './CommandPalette';
import { Sidebar } from './Sidebar';
import { useConnectionsGate } from './useConnectionsGate';
import { MOBILE_QUERY, NARROW_QUERY, useMediaQuery } from './useMediaQuery';
import { usePageTitle } from './usePageTitle';
import { useSidebarRail } from './useSidebarRail';

/**
 * The signed-in layout: sidebar (full / icon rail) + main column on desktop;
 * a header with a navigation drawer below 768px. ⌘K opens the palette.
 */
export function AppShell() {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const [railPreferred, toggleRail] = useSidebarRail();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // The opener sits in the region made inert while the drawer is open, which
  // blurs it; restore focus to it explicitly on close.
  const menuButton = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const title = usePageTitle();
  useConnectionsGate();

  // The drawer closes on navigation and when the viewport grows to desktop.
  const [drawerPath, setDrawerPath] = useState(pathname);
  if (drawerOpen && (drawerPath !== pathname || !isMobile)) {
    setDrawerOpen(false);
  }
  if (drawerPath !== pathname) setDrawerPath(pathname);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, []);

  const handleLogout = useCallback(() => {
    void logout(queryClient, () => navigate({ to: '/login' }));
  }, [queryClient, navigate]);

  // Exiting a support session ends it server-side; the admin console opened
  // this tab, so it can close. If the browser refuses, /login is where logout lands.
  const exitSupport = useCallback(() => {
    void logout(queryClient, () => navigate({ to: '/login' })).then(() => { window.close(); });
  }, [queryClient, navigate]);

  const openSearch = () => {
    setDrawerOpen(false);
    setPaletteOpen(true);
  };
  const rail = isNarrow || railPreferred;

  return (
    <TourProvider>
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded-ctl bg-surface px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        Skip to content
      </a>
      {isMobile ? null : (
        <div className="sticky top-0 h-dvh shrink-0 border-r border-line">
          <Sidebar rail={rail} {...(isNarrow ? {} : { onToggleRail: toggleRail })} onOpenSearch={openSearch} onLogout={handleLogout} />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col" inert={isMobile && drawerOpen}>
        {isMobile ? (
          <header className="sticky top-0 z-30 flex h-(--header-h) items-center gap-3 border-b border-line bg-bg px-4">
            <button
              ref={menuButton}
              type="button"
              onClick={() => { setDrawerOpen(true); }}
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              data-tour="nav-menu"
              className="grid size-8 place-items-center rounded-ctl border border-line bg-surface hover:bg-wash"
            >
              <Menu className="size-4" aria-hidden="true" />
            </button>
            <span className="truncate text-base font-semibold" aria-hidden="true">{title}</span>
          </header>
        ) : null}
        <Banners onExitSupport={exitSupport} />
        <main id="main" tabIndex={-1} className="flex min-w-0 flex-1 flex-col outline-none">
          <Outlet />
        </main>
      </div>

      {isMobile ? (
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} title="Navigation" side="left" width="var(--side)" bare returnFocusTo={menuButton}>
          <Sidebar rail={false} onClose={() => { setDrawerOpen(false); }} onOpenSearch={openSearch} onLogout={handleLogout} />
        </Drawer>
      ) : null}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onLogout={handleLogout} />
    </div>
    </TourProvider>
  );
}
